import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import InfoPage from "../InfoPage";
import UploadItineraryModal from "./UploadItineraryModal";
import ModalPortal from '../../components/Modal/ModalPortal';
import { getAuthToken } from '../../utils/authHelpers';
import { authFetch } from '../../utils/authFetch';
import { getCloudinaryUrl } from '../../utils/cloudinary';
import BlurredGate from '../../components/BlurredGate';

const HeartIcon = ({ isLiked, size = 14 }) => (
  <svg 
    width={size} 
    height={size} 
    viewBox="0 0 24 24" 
    fill="none" 
    xmlns="http://www.w3.org/2000/svg"
    style={{ display: 'inline-block', verticalAlign: 'middle' }}
  >
    <path 
      fillRule="evenodd" 
      clipRule="evenodd" 
      d="M8.55284 3.00012C7.93598 3.00012 7.23841 3.06514 6.57209 3.29224C2.55494 4.60387 1.26341 8.894 2.39877 12.43L2.40354 12.4448L2.40877 12.4595C3.03435 14.2174 4.04226 15.8127 5.35336 17.1249L5.36091 17.1324L5.36862 17.1398C7.23782 18.9323 9.27254 20.4953 11.4756 21.8515L11.9934 22.1703L12.5147 21.8573C14.7226 20.5315 16.7964 18.9254 18.6432 17.1474L18.649 17.1419L18.6547 17.1362C19.9771 15.8215 20.9851 14.2144 21.6015 12.4549L21.6066 12.4402L21.6113 12.4253C22.7251 8.89703 21.4401 4.60176 17.4507 3.30948C16.7976 3.09221 16.1236 3.00012 15.4648 3.00012C13.9828 3.00011 12.8858 3.62064 12.0004 4.25309C11.1219 3.62545 10.0176 3.00012 8.55284 3.00012Z" 
      fill={isLiked ? '#dc1d08' : '#999'}
    />
  </svg>
);

const ItineraryCard = ({
  img,
  title,
  location,
  duration,
  handle,
  fallback,
  onClick,
  likeCount = 0,
  isLiked = false,
  itineraryId,
  onLikeToggle,
  isOwner = false,
  onEdit
}) => {
  const handleHeartClick = async (e) => {
    e.stopPropagation(); // Prevent card click
    if (!onLikeToggle) return;
    
    const token = getAuthToken();
    if (!token) {
      alert('Please log in to like itineraries');
      return;
    }
    
    try {
      const base = window.location.hostname === 'localhost' ? 'http://localhost:4000' : '';
      const payload = { itinerary_id: itineraryId };
      
      const res = await authFetch(`${base}/api/collab/like`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(payload)
      });
      
      const data = await res.json();
      if (res.ok && data.ok) {
        onLikeToggle(data.liked, data.like_count);
      } else {
        console.error('Failed to toggle like');
      }
    } catch (err) {
      // Don't log auth errors since authFetch handles logout
      if (!err.message?.includes('Unauthorized') && !err.message?.includes('expired')) {
        console.error('Like toggle error:', err);
      }
    }
  };

  const optimizedImageUrl = img ? getCloudinaryUrl(img, {
    width: 450,
    height: 600,
    crop: 'fill',
    quality: 'auto',
    format: 'auto'
  }) : null;

  return (
    <div 
      style={{ display: 'grid', justifyItems: 'stretch', gap: 8, width: '100%', cursor: onClick ? 'pointer' : 'default' }}
      onClick={onClick}
    >
      <img
        src={optimizedImageUrl || img}
        alt={title}
        referrerPolicy="no-referrer"
        crossOrigin="anonymous"
        onError={(e) => {
          if (fallback && e?.currentTarget?.src !== window.location.origin + fallback && !e.currentTarget.dataset.fallbackApplied) {
            e.currentTarget.dataset.fallbackApplied = '1';
            e.currentTarget.src = fallback;
          }
        }}
        style={{ width: '100%', aspectRatio: '3 / 4', height: 'auto', objectFit: 'cover', borderRadius: 12, boxShadow: '0 2px 8px rgba(0,0,0,.08)', display: 'block', transition: 'transform 0.2s, box-shadow 0.2s' }}
        onMouseEnter={(e) => { if (onClick) { e.currentTarget.style.transform = 'scale(1.02)'; e.currentTarget.style.boxShadow = '0 4px 12px rgba(0,0,0,.12)'; }}}
        onMouseLeave={(e) => { if (onClick) { e.currentTarget.style.transform = 'scale(1)'; e.currentTarget.style.boxShadow = '0 2px 8px rgba(0,0,0,.08)'; }}}
      />
      <div style={{ fontFamily: "'Neue Einstellung', 'Poppins', sans-serif", fontSize: 14, fontWeight: 600, color: '#0f084b', display: 'grid', gridTemplateColumns: '1fr auto', alignItems: 'center', gap: '6px' }}>
        <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', minWidth: 0 }}>{title}</span>
        <div style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
          {isOwner && onEdit && (
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                onEdit();
              }}
              style={{
                background: 'transparent',
                color: '#0f084b',
                border: '1px solid #0f084b',
                borderRadius: 999,
                fontSize: 11,
                padding: '2px 8px',
                fontFamily: "'Neue Einstellung', 'Poppins', sans-serif",
                cursor: 'pointer',
                flexShrink: 0
              }}
            >
              Edit
            </button>
          )}
          <span 
            onClick={handleHeartClick}
            style={{ 
              display: 'inline-flex',
              alignItems: 'center',
              gap: '4px',
              cursor: 'pointer',
              flexShrink: 0
            }}
          >
            <HeartIcon isLiked={isLiked} size={14} />
            {likeCount > 0 && (
              <span style={{ 
                fontSize: '12px', 
                color: isLiked ? '#dc1d08' : '#999',
                fontFamily: "'Neue Einstellung', 'Poppins', sans-serif"
              }}>
                {likeCount}
              </span>
            )}
          </span>
        </div>
      </div>
      {handle && (
        <div style={{ fontFamily: "'Neue Einstellung', 'Poppins', sans-serif", fontSize: 12, color: '#0f084b', opacity: .9 }}>@{handle}</div>
      )}
      <div style={{ fontFamily: "'Neue Einstellung', 'Poppins', sans-serif", fontSize: 12, color: '#0f084b', opacity: .8 }}>{location}</div>
      {duration && (
        <div style={{ fontFamily: "'Neue Einstellung', 'Poppins', sans-serif", fontSize: 11, color: '#dc1d08', opacity: .9 }}>{duration}</div>
      )}
    </div>
  );
};

const ItineraryModal = ({
  isOpen,
  onClose,
  title,
  description,
  image,
  location,
  duration,
  highlights,
  activities,
  pdf_url,
  external_url,
  isOwner,
  onEdit,
  onDelete
}) => {
  if (!title) return null;

  const optimizedModalImage = image ? getCloudinaryUrl(image, {
    width: 1200,
    height: 675,
    crop: 'fill',
    quality: 'auto',
    format: 'auto'
  }) : image;
  
  return (
    <ModalPortal isOpen={isOpen} onClose={onClose} maxWidth="700px" maxHeight="85vh">
        {image && (
          <img 
            src={optimizedModalImage} 
            alt={title}
            style={{
              width: '100%',
              height: 350,
              objectFit: 'cover',
              borderRadius: '16px 16px 0 0'
            }}
          />
        )}
        
        <div style={{ padding: '32px' }}>
          <h2 style={{
            fontFamily: "'Sailors', serif",
            fontSize: 28,
            color: '#0f084b',
            marginBottom: 8,
            marginTop: 0
          }}>
            {title}
          </h2>
          
          <div style={{ display: 'flex', gap: 20, marginBottom: 24, flexWrap: 'wrap' }}>
            {location && (
              <div style={{
                fontFamily: "'Neue Einstellung', 'Poppins', sans-serif",
                fontSize: 14,
                color: '#0f084b',
                opacity: 0.8
              }}>
                📍 {location}
              </div>
            )}
            {duration && (
              <div style={{
                fontFamily: "'Neue Einstellung', 'Poppins', sans-serif",
                fontSize: 14,
                color: '#dc1d08',
                fontWeight: 600
              }}>
                ⏱ {duration}
              </div>
            )}
          </div>
          
          <div style={{
            fontFamily: "'Neue Einstellung', 'Poppins', sans-serif",
            fontSize: 16,
            color: '#0f084b',
            lineHeight: 1.8,
            whiteSpace: 'pre-wrap',
            marginBottom: 24
          }}>
            {description || 'No itinerary details provided yet.'}
          </div>
          
          {highlights && highlights.length > 0 && (
            <div style={{ marginBottom: 24 }}>
              <h3 style={{
                fontFamily: "'Sailors', serif",
                fontSize: 18,
                color: '#0f084b',
                marginBottom: 12
              }}>
                Highlights
              </h3>
              <ul style={{
                fontFamily: "'Neue Einstellung', 'Poppins', sans-serif",
                fontSize: 15,
                color: '#0f084b',
                lineHeight: 1.8,
                paddingLeft: 20
              }}>
                {highlights.map((highlight, index) => (
                  <li key={index} style={{ marginBottom: 8 }}>{highlight}</li>
                ))}
              </ul>
            </div>
          )}
          
          {activities && activities.length > 0 && (
            <div style={{ marginBottom: 24 }}>
              <h3 style={{
                fontFamily: "'Sailors', serif",
                fontSize: 18,
                color: '#0f084b',
                marginBottom: 12
              }}>
                Activities Included
              </h3>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                {activities.map((activity, index) => (
                  <span 
                    key={index} 
                    style={{
                      backgroundColor: '#f5f5f5',
                      color: '#0f084b',
                      padding: '6px 12px',
                      borderRadius: 20,
                      fontFamily: "'Neue Einstellung', 'Poppins', sans-serif",
                      fontSize: 13
                    }}
                  >
                    {activity}
                  </span>
                ))}
              </div>
            </div>
          )}
          
          <div style={{
            display: 'flex',
            gap: 16,
            justifyContent: 'center',
            flexWrap: 'wrap',
            marginTop: 32
          }}>
            {(pdf_url || external_url) ? (
              <>
                {pdf_url && (
                  <button
                    onClick={() => window.open(pdf_url, '_blank')}
                    style={{
                      backgroundColor: '#dc1d08',
                      color: 'white',
                      border: 'none',
                      borderRadius: 8,
                      padding: '14px 32px',
                      fontFamily: "'Neue Einstellung', 'Poppins', sans-serif",
                      fontSize: 16,
                      fontWeight: 600,
                      cursor: 'pointer',
                      transition: 'all 0.2s ease',
                      minWidth: 160
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.backgroundColor = '#b51706';
                      e.currentTarget.style.transform = 'translateY(-1px)';
                      e.currentTarget.style.boxShadow = '0 4px 8px rgba(220, 29, 8, 0.2)';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.backgroundColor = '#dc1d08';
                      e.currentTarget.style.transform = 'translateY(0)';
                      e.currentTarget.style.boxShadow = 'none';
                    }}
                  >
                    View Itinerary (PDF)
                  </button>
                )}
                {external_url && (
                  <button
                    onClick={() => window.open(external_url, '_blank')}
                    style={{
                      backgroundColor: '#dc1d08',
                      color: 'white',
                      border: 'none',
                      borderRadius: 8,
                      padding: '14px 32px',
                      fontFamily: "'Neue Einstellung', 'Poppins', sans-serif",
                      fontSize: 16,
                      fontWeight: 600,
                      cursor: 'pointer',
                      transition: 'all 0.2s ease',
                      minWidth: 160
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.backgroundColor = '#b51706';
                      e.currentTarget.style.transform = 'translateY(-1px)';
                      e.currentTarget.style.boxShadow = '0 4px 8px rgba(220, 29, 8, 0.2)';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.backgroundColor = '#dc1d08';
                      e.currentTarget.style.transform = 'translateY(0)';
                      e.currentTarget.style.boxShadow = 'none';
                    }}
                  >
                    View Itinerary (Link)
                  </button>
                )}
              </>
            ) : (
              <button
                style={{
                  backgroundColor: '#dc1d08',
                  color: 'white',
                  border: 'none',
                  borderRadius: 8,
                  padding: '14px 32px',
                  fontFamily: "'Neue Einstellung', 'Poppins', sans-serif",
                  fontSize: 16,
                  fontWeight: 600,
                  cursor: 'pointer',
                  transition: 'all 0.2s ease',
                  minWidth: 160
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor = '#b51706';
                  e.currentTarget.style.transform = 'translateY(-1px)';
                  e.currentTarget.style.boxShadow = '0 4px 8px rgba(220, 29, 8, 0.2)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = '#dc1d08';
                  e.currentTarget.style.transform = 'translateY(0)';
                  e.currentTarget.style.boxShadow = 'none';
                }}
              >
                Book This Itinerary
              </button>
            )}
            
            <button
              style={{
                backgroundColor: 'transparent',
                color: '#0f084b',
                border: '2px solid #0f084b',
                borderRadius: 8,
                padding: '14px 32px',
                fontFamily: "'Neue Einstellung', 'Poppins', sans-serif",
                fontSize: 16,
                fontWeight: 600,
                cursor: 'pointer',
                transition: 'all 0.2s ease',
                minWidth: 160
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = '#0f084b';
                e.currentTarget.style.color = 'white';
                e.currentTarget.style.transform = 'translateY(-1px)';
                e.currentTarget.style.boxShadow = '0 4px 8px rgba(15, 8, 75, 0.2)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = 'transparent';
                e.currentTarget.style.color = '#0f084b';
                e.currentTarget.style.transform = 'translateY(0)';
                e.currentTarget.style.boxShadow = 'none';
              }}
            >
              Learn More
            </button>
            
            {isOwner && (
              <button
                style={{
                  backgroundColor: 'transparent',
                  color: '#0f084b',
                  border: '2px solid #0f084b',
                  borderRadius: 8,
                  padding: '14px 32px',
                  fontFamily: "'Neue Einstellung', 'Poppins', sans-serif",
                  fontSize: 16,
                  fontWeight: 600,
                  cursor: 'pointer',
                  transition: 'all 0.2s ease',
                  minWidth: 160
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor = '#0f084b';
                  e.currentTarget.style.color = 'white';
                  e.currentTarget.style.transform = 'translateY(-1px)';
                  e.currentTarget.style.boxShadow = '0 4px 8px rgba(15, 8, 75, 0.2)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = 'transparent';
                  e.currentTarget.style.color = '#0f084b';
                  e.currentTarget.style.transform = 'translateY(0)';
                  e.currentTarget.style.boxShadow = 'none';
                }}
                onClick={onEdit}
              >
                Edit Itinerary
              </button>
            )}

            {isOwner && (
              <button
                style={{
                  backgroundColor: 'transparent',
                  color: '#dc1d08',
                  border: '2px solid #dc1d08',
                  borderRadius: 8,
                  padding: '14px 32px',
                  fontFamily: "'Neue Einstellung', 'Poppins', sans-serif",
                  fontSize: 16,
                  fontWeight: 600,
                  cursor: 'pointer',
                  transition: 'all 0.2s ease',
                  minWidth: 160
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor = '#dc1d08';
                  e.currentTarget.style.color = 'white';
                  e.currentTarget.style.transform = 'translateY(-1px)';
                  e.currentTarget.style.boxShadow = '0 4px 8px rgba(220, 29, 8, 0.2)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = 'transparent';
                  e.currentTarget.style.color = '#dc1d08';
                  e.currentTarget.style.transform = 'translateY(0)';
                  e.currentTarget.style.boxShadow = 'none';
                }}
                onClick={() => {
                  if (window.confirm('Are you sure you want to delete this itinerary?')) {
                    onDelete();
                  }
                }}
              >
                Delete Itinerary
              </button>
            )}
          </div>
        </div>
    </ModalPortal>
  );
};

const Itineraries = () => {
  const navigate = useNavigate();
  const [authorized, setAuthorized] = useState(null);
  const [membershipTier, setMembershipTier] = useState(null);
  const [email, setEmail] = useState('');
  const [data, setData] = useState([]);
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedItinerary, setSelectedItinerary] = useState(null);
  const [editingItinerary, setEditingItinerary] = useState(null);
  const [isHostOrCreator, setIsHostOrCreator] = useState(false);
  const [uploadModalOpen, setUploadModalOpen] = useState(false);
  const [searchInput, setSearchInput] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [filteredData, setFilteredData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  const [randomSeed, setRandomSeed] = useState(null);

  // Search filtering function
  const filterItineraries = (itineraries, searchTerm) => {
    if (!searchTerm) return itineraries;
    
    const lowerSearch = searchTerm.toLowerCase();
    return itineraries.filter(itinerary => {
      return (
        itinerary.title?.toLowerCase().includes(lowerSearch) ||
        itinerary.location?.toLowerCase().includes(lowerSearch) ||
        itinerary.destination?.toLowerCase().includes(lowerSearch) ||
        itinerary.description?.toLowerCase().includes(lowerSearch) ||
        itinerary.highlights?.some(h => h.toLowerCase().includes(lowerSearch)) ||
        itinerary.activities?.some(a => a.toLowerCase().includes(lowerSearch)) ||
        itinerary.duration?.toLowerCase().includes(lowerSearch)
      );
    });
  };

  // Handle search input changes with debouncing
  useEffect(() => {
    const delayDebounceFn = setTimeout(() => {
      setSearchTerm(searchInput);
    }, 300); // 300ms delay

    return () => clearTimeout(delayDebounceFn);
  }, [searchInput]);

  // Filter data when search term changes
  useEffect(() => {
    const filtered = filterItineraries(data, searchTerm);
    setFilteredData(filtered);
  }, [searchTerm, data]);

  // Handle itineraryId deep-linking from URL params
  useEffect(() => {
    const handleItineraryDeepLink = () => {
      const params = new URLSearchParams(window.location.search);
      const itineraryId = params.get('itineraryId');
      
      if (!itineraryId || data.length === 0) {
        return;
      }

      const itinerary = data.find(i => i.id === itineraryId);

      if (itinerary) {
        setSelectedItinerary(itinerary);
        setModalOpen(true);
        
        // Clean up URL after opening (remove itineraryId param)
        const newParams = new URLSearchParams(window.location.search);
        newParams.delete('itineraryId');
        const newUrl = newParams.toString() 
          ? `${window.location.pathname}?${newParams.toString()}`
          : window.location.pathname;
        window.history.replaceState({}, '', newUrl);
      }
    };

    handleItineraryDeepLink();
  }, [data]);

  // Handle clear search
  const handleClearSearch = () => {
    setSearchInput('');
    setSearchTerm('');
  };

  const isItineraryOwner = (itinerary) =>
    Boolean(
      itinerary?.author_email &&
      email &&
      itinerary.author_email.toLowerCase() === email.toLowerCase()
    );

  // Handle window resize for mobile detection
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    let e = params.get('email') || '';
    if (!e) {
      try { e = localStorage.getItem('postaway_email') || ''; } catch (_) {}
    }
    setEmail(e);
    
    if (!e) {
      console.warn('Itineraries: No email found. Access may be limited.');
      setAuthorized(false);
      return;
    }
    
    // Store email for future use
    try {
      localStorage.setItem('postaway_email', e);
    } catch (_) {}
    
    // Check if user has an active traveler subscription or is a host/creator
    (async () => {
      try {
        const res = await fetch('/api/access/subscription', { 
          method: 'POST', 
          headers: { 'Content-Type': 'application/json' }, 
          body: JSON.stringify({ email: e }) 
        });
        const data = await res.json();
        
        // Check if user has active subscription and is a traveler, host, creator, or partner
        const roles = data.roles || [];
        const isHostOrCreatorUser = roles.some(r => r === 'creator' || r === 'host');
        const isTraveler = roles.includes('traveler');
        const isPartner = roles.includes('partner');
        
        if (res.ok && data.ok && data.active && (isTraveler || isHostOrCreatorUser || isPartner)) {
          setMembershipTier(data.membershipTier || 'all_access');
          const isBasic = data.membershipTier === 'basic';
          if (isBasic) {
            setAuthorized('basic');
          } else {
            setAuthorized(true);
          }
          setIsHostOrCreator(isHostOrCreatorUser);
          
          // Fetch real itineraries for both basic and all-access
          try {
            const emailParam = e ? `?email=${encodeURIComponent(e)}` : '';
            const itinerariesRes = await fetch(`/api/itineraries${emailParam}`, {
              method: 'GET',
              headers: { 'Content-Type': 'application/json' }
            });
            const itinerariesData = await itinerariesRes.json();
            
            if (itinerariesData.ok) {
              const realData = itinerariesData.itineraries || [];
              if (itinerariesData.seed !== undefined && itinerariesData.seed !== null) {
                setRandomSeed(itinerariesData.seed);
              }
              setData(realData);
              setFilteredData(realData);
            } else {
              // If fetching fails, use empty array
              setData([]);
              setFilteredData([]);
            }
          } catch (error) {
            console.error('Error fetching itineraries:', error);
            setData([]);
            setFilteredData([]);
          } finally {
            setLoading(false);
          }
        } else {
          // Redirect to home page if no active subscription or not a traveler/host/creator
          navigate('/');
        }
      } catch (err) {
        console.error('Authorization check failed:', err);
        // Redirect to home page on error
        navigate('/');
      }
    })();
  }, [navigate]);

  if (authorized === null || loading) {
    return <InfoPage belowHeader={<div style={{ textAlign: 'center', fontFamily: "'Neue Einstellung', 'Poppins', sans-serif", color: '#0f084b' }}>Loading itineraries…</div>} disableStamps tallOverride={false} customMinHeight={600} showInlineLivePulse />;
  }

  const fetchItineraries = async () => {
    try {
      const queryParams = new URLSearchParams();
      if (email) queryParams.set('email', email);
      if (randomSeed !== null) queryParams.set('seed', String(randomSeed));
      const queryString = queryParams.toString();
      const itinerariesRes = await fetch(`/api/itineraries${queryString ? `?${queryString}` : ''}`, {
        method: 'GET',
        headers: { 'Content-Type': 'application/json' }
      });
      const itinerariesData = await itinerariesRes.json();
      
      if (itinerariesData.ok) {
        const realData = itinerariesData.itineraries || [];
        if (itinerariesData.seed !== undefined && itinerariesData.seed !== null) {
          setRandomSeed(itinerariesData.seed);
        }
        setData(realData);
        setFilteredData(realData);
      }
    } catch (error) {
      console.error('Error fetching itineraries:', error);
    }
  };

  // Handle like toggle
  const handleLikeToggle = (isLiked, likeCount, itineraryId) => {
    setData(prevData => {
      const updateItem = (item) => {
        if (item.id === itineraryId) {
          return { ...item, is_liked: isLiked, like_count: likeCount };
        }
        return item;
      };
      
      return prevData.map(updateItem);
    });
    
    // Also update filtered data
    setFilteredData(prevData => {
      const updateItem = (item) => {
        if (item.id === itineraryId) {
          return { ...item, is_liked: isLiked, like_count: likeCount };
        }
        return item;
      };
      
      return prevData.map(updateItem);
    });
  };

  const handleUploadSuccess = async (result) => {
    // Show success message
    console.log('Itinerary uploaded successfully:', result);
    setEditingItinerary(null);
    
    // Refresh the itineraries list
    await fetchItineraries();
  };

  const handleEditItinerary = (itinerary) => {
    setModalOpen(false);
    setSelectedItinerary(null);
    setEditingItinerary(itinerary);
    setUploadModalOpen(true);
  };

  const below = (
    <div style={{ 
      gridColumn: '1 / -1',
      display: 'block',
      width: '100%',
      padding: isMobile ? '0 8px' : '0 24px',
      margin: '12px 0 0 0'
    }}>
      <div style={{ 
        display: 'flex', 
        flexDirection: 'column', 
        gap: isMobile ? 24 : 36, 
        alignItems: 'center' 
      }}>
        {/* Page Title */}
        <div style={{ textAlign: 'center', marginTop: 20 }}>
          <h1 style={{ 
            fontFamily: "'Sailors', serif", 
            fontSize: isMobile ? 28 : 36, 
            color: '#0f084b', 
            marginBottom: 8,
            padding: isMobile ? '0 16px' : '0'
          }}>
            Travel Itineraries
          </h1>
          <p style={{ 
            fontFamily: "'Neue Einstellung', 'Poppins', sans-serif", 
            fontSize: isMobile ? 14 : 16, 
            color: '#0f084b', 
            opacity: 0.8,
            padding: isMobile ? '0 16px' : '0'
          }}>
           Discover curated experiences by your favorite creators
          </p>
        </div>

        {/* Search Bar */}
        <div style={{ 
          width: '100%', 
          maxWidth: '700px', 
          paddingLeft: isMobile ? '16px' : '40px', 
          paddingRight: isMobile ? '16px' : '40px' 
        }}>
          <div style={{ position: 'relative', width: '100%' }}>
            <input
              type="text"
              placeholder="Search itineraries by location, activities, or description..."
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              style={{
                width: '100%',
                padding: '12px 16px',
                paddingRight: searchInput ? '40px' : '16px',
                borderRadius: '100px',
                border: '1px solid #E6E8EC',
                fontFamily: "'Neue Einstellung', 'Poppins', sans-serif",
                fontSize: '14px',
                outline: 'none',
                transition: 'border-color 0.2s'
              }}
              onFocus={(e) => e.target.style.borderColor = '#141416'}
              onBlur={(e) => e.target.style.borderColor = '#E6E8EC'}
            />
            {searchInput && (
              <button
                onClick={handleClearSearch}
                style={{
                  position: 'absolute',
                  right: '12px',
                  top: '50%',
                  transform: 'translateY(-50%)',
                  background: 'none',
                  border: 'none',
                  cursor: 'pointer',
                  padding: '4px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}
              >
                <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                  <path d="M12 4L4 12" stroke="#777E90" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  <path d="M4 4L12 12" stroke="#777E90" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </button>
            )}
          </div>
        </div>

        {/* Upload Itinerary Button - Only visible to hosts and creators */}
        {isHostOrCreator && (
          <div style={{ textAlign: 'center', marginTop: -20 }}>
            <button
              style={{
                backgroundColor: '#dc1d08',
                color: 'white',
                border: 'none',
                borderRadius: 8,
                padding: isMobile ? '12px 24px' : '14px 32px',
                fontFamily: "'Neue Einstellung', 'Poppins', sans-serif",
                fontSize: isMobile ? 14 : 16,
                fontWeight: 600,
                cursor: 'pointer',
                transition: 'all 0.2s ease',
                boxShadow: '0 2px 8px rgba(220, 29, 8, 0.15)'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = '#b51706';
                e.currentTarget.style.transform = 'translateY(-1px)';
                e.currentTarget.style.boxShadow = '0 4px 12px rgba(220, 29, 8, 0.25)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = '#dc1d08';
                e.currentTarget.style.transform = 'translateY(0)';
                e.currentTarget.style.boxShadow = '0 2px 8px rgba(220, 29, 8, 0.15)';
              }}
              onClick={() => {
                setEditingItinerary(null);
                setUploadModalOpen(true);
              }}
            >
              Upload Itinerary
            </button>
          </div>
        )}

        {/* Show message when no results */}
        {searchTerm && filteredData.length === 0 && (
          <div style={{ textAlign: 'center', padding: isMobile ? '32px 16px' : '40px 20px', width: '100%' }}>
            <p style={{ 
              fontFamily: "'Neue Einstellung', 'Poppins', sans-serif", 
              fontSize: isMobile ? 14 : 16, 
              color: '#0f084b', 
              opacity: 0.7 
            }}>
              No itineraries found matching "{searchTerm}"
            </p>
            <button
              onClick={handleClearSearch}
              style={{
                marginTop: '16px',
                backgroundColor: 'transparent',
                color: '#dc1d08',
                border: '2px solid #dc1d08',
                borderRadius: 8,
                padding: '8px 20px',
                fontFamily: "'Neue Einstellung', 'Poppins', sans-serif",
                fontSize: 14,
                fontWeight: 600,
                cursor: 'pointer',
                transition: 'all 0.2s ease'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = '#dc1d08';
                e.currentTarget.style.color = 'white';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = 'transparent';
                e.currentTarget.style.color = '#dc1d08';
              }}
            >
              Clear Search
            </button>
          </div>
        )}

        {/* Unified Itineraries */}
        {filteredData.length > 0 && (
        <div style={{ width: '100%' }}>
          <div style={{ 
            fontFamily: "'Sailors', serif", 
            fontSize: isMobile ? 20 : 24, 
            color: '#0f084b', 
            margin: isMobile ? '6px 0 12px 0' : '6px 0 16px 6px',
            textAlign: isMobile ? 'center' : 'left',
            padding: isMobile ? '0 16px' : '0'
          }}>
            Itineraries
            {searchTerm && (
              <span style={{ fontSize: 14, fontWeight: 'normal', opacity: 0.7, marginLeft: 8 }}>
                ({filteredData.length} found)
              </span>
            )}
          </div>
          <div style={{ padding: isMobile ? '0' : '0 40px' }}>
            <div style={{ 
              display: 'grid', 
              gridTemplateColumns: isMobile ? '1fr' : 'repeat(4, 1fr)', 
              gap: isMobile ? 16 : 24, 
              justifyItems: 'stretch', 
              alignItems: 'start',
              padding: isMobile ? '0 16px' : '0'
            }}>
              {filteredData.map((itinerary) => (
                  <ItineraryCard 
                    key={`itinerary-${itinerary.id}`}
                    img={itinerary.image}
                    title={itinerary.title}
                    location={itinerary.location}
                    duration={itinerary.duration}
                    handle={itinerary.creatorHandle || itinerary.hostHandle || ''}
                    fallback="/lovable-uploads/1.png"
                    likeCount={itinerary.like_count || 0}
                    isLiked={itinerary.is_liked || false}
                    itineraryId={itinerary.id}
                    isOwner={isItineraryOwner(itinerary)}
                    onEdit={() => handleEditItinerary(itinerary)}
                    onLikeToggle={(liked, count) => handleLikeToggle(liked, count, itinerary.id)}
                    onClick={() => {
                      setSelectedItinerary(itinerary);
                      setModalOpen(true);
                    }}
                  />
              ))}
            </div>
          </div>
        </div>
        )}
      </div>
    </div>
  );

  if (authorized === 'basic') {
    const userType = (typeof window !== 'undefined' && window.localStorage.getItem('postaway_user_type')) || 'traveler';
    return (
      <>
        <InfoPage below={
          <BlurredGate userType={userType}>
            {below}
          </BlurredGate>
        } disableStamps tallOverride={false} customMinHeight={820} showInlineLivePulse />
      </>
    );
  }

  return (
    <>
      <InfoPage below={below} disableStamps tallOverride={false} customMinHeight={820} showInlineLivePulse />
      <ItineraryModal 
        isOpen={modalOpen}
        onClose={() => {
          setModalOpen(false);
          setSelectedItinerary(null);
        }}
        title={selectedItinerary?.title}
        description={selectedItinerary?.description}
        image={selectedItinerary?.image}
        location={selectedItinerary?.location}
        duration={selectedItinerary?.duration}
        highlights={selectedItinerary?.highlights}
        activities={selectedItinerary?.activities}
        pdf_url={selectedItinerary?.pdf_url}
        external_url={selectedItinerary?.external_url}
        isOwner={isItineraryOwner(selectedItinerary)}
        onEdit={() => {
          if (selectedItinerary) {
            handleEditItinerary(selectedItinerary);
          }
        }}
        onDelete={async () => {
          try {
            const response = await fetch('/api/itinerary/delete', {
              method: 'DELETE',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({
                email,
                itinerary_id: selectedItinerary?.id,
                type: selectedItinerary?.type
              })
            });
            
            const result = await response.json();
            
            if (result.ok) {
              // Close modal
              setModalOpen(false);
              setSelectedItinerary(null);
              
              // Remove from UI
              setData(prev => prev.filter(i => i.id !== selectedItinerary?.id));
              setFilteredData(prev => prev.filter(i => i.id !== selectedItinerary?.id));
            } else {
              alert('Failed to delete itinerary: ' + (result.message || result.error));
            }
          } catch (error) {
            console.error('Error deleting itinerary:', error);
            alert('Failed to delete itinerary');
          }
        }}
      />
      <UploadItineraryModal
        isOpen={uploadModalOpen}
        onClose={() => {
          setUploadModalOpen(false);
          setEditingItinerary(null);
        }}
        email={email}
        onSuccess={handleUploadSuccess}
        editItinerary={editingItinerary}
      />
    </>
  );
};

export default Itineraries;
